import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Building2, ChevronDown, Plus, Check, Loader2, Globe, ShieldCheck } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatCountryOption } from "@/lib/countryLabel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export default function BusinessSwitcher() {
  const { user, switchBusiness } = useAuth();
  const { t, dict } = useLang();
  const [data, setData] = useState({ businesses: [], active_business_id: null, can_add: false });
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", country_code: "GB", restaurant_type: "" });

  const load = useCallback(async () => {
    try { const { data } = await api.get("/businesses"); setData(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { if (user?.role && ["owner", "manager", "staff"].includes(user.role)) load(); }, [user?.id, user?.role, user?.restaurant_id, load]);
  useEffect(() => { if (addOpen && !countries.length) api.get("/public/signup-options").then((r) => setCountries(r.data.countries || [])).catch(() => {}); }, [addOpen, countries.length]);
  useEffect(() => { if (user?.role === "owner") api.get("/public/signup-options").then((r) => setCountries(r.data.countries || [])).catch(() => {}); }, [user?.role]);

  if (!user || !["owner", "manager", "staff"].includes(user.role)) return null;
  const list = data.businesses || [];
  // Managers/staff have exactly one business — show a static badge, no switching.
  const single = list.length <= 1 && !data.can_add;
  const active = list.find((b) => b.id === data.active_business_id) || list[0];

  const pick = async (id) => {
    setOpen(false);
    if (id === data.active_business_id) return;
    await switchBusiness(id);
    await load();
    toast.success(t("Business switched"));
  };

  const addBusiness = async () => {
    if (!form.name.trim()) { toast.error(t("Business name is required")); return; }
    setBusy(true);
    try {
      const { data: biz } = await api.post("/businesses", { name: form.name.trim(), country_code: form.country_code, restaurant_type: form.restaurant_type || null });
      toast.success(t("Business added"));
      setAddOpen(false); setForm({ name: "", country_code: "GB", restaurant_type: "" });
      await load();
      await switchBusiness(biz.id); // jump straight into the new business
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 relative" data-testid="business-switcher">
      <button type="button" disabled={single} onClick={() => setOpen((o) => !o)} data-testid="business-switcher-btn"
        className={`w-full flex items-center gap-2.5 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-left transition-colors ${single ? "" : "hover:border-[#D4AF37]/40 cursor-pointer"}`}>
        <Building2 className="w-4 h-4 text-[#D4AF37] shrink-0" />
        <span className="flex-1 min-w-0">
          <span className="block text-sm text-[#F3F4F6] truncate">{active?.name || user.restaurant || "—"}</span>
          <span className="block text-[10px] uppercase tracking-wider text-white/35 truncate">{active ? `${active.country} · ${active.currency?.code || ""}` : ""}</span>
        </span>
        {!single && <ChevronDown className={`w-4 h-4 text-white/40 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>

      {open && !single && (
        <div className="absolute z-50 mt-2 left-0 right-0 rounded-lg border border-white/10 bg-[#141416] shadow-2xl overflow-hidden" data-testid="business-switcher-menu">
          <div className="max-h-72 overflow-y-auto py-1">
            {list.map((b) => (
              <button key={b.id} onClick={() => pick(b.id)} data-testid={`business-option-${b.id}`}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-white/[0.04] transition-colors">
                <Globe className="w-3.5 h-3.5 text-white/30 shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-[#F3F4F6] truncate">{b.name}</span>
                  <span className="block text-[10px] text-white/40 truncate flex items-center gap-1">
                    {b.country} · {b.currency?.code}
                    {b.payroll_verified && <ShieldCheck className="w-3 h-3 text-[#8A9A5B]" />}
                  </span>
                </span>
                {b.id === data.active_business_id && <Check className="w-4 h-4 text-[#D4AF37] shrink-0" />}
              </button>
            ))}
          </div>
          {data.can_add && (
            <button onClick={() => { setOpen(false); setAddOpen(true); }} data-testid="add-business-btn"
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[#D4AF37] border-t border-white/10 hover:bg-[#D4AF37]/10 transition-colors">
              <Plus className="w-4 h-4" /> {t("Add business")}
            </button>
          )}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={(o) => !o && setAddOpen(false)}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md" data-testid="add-business-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-lux text-2xl font-light">{t("Add a business")}</DialogTitle>
            <DialogDescription className="text-[#9CA3AF]">{t("Each business keeps its own staff, payroll and data. The country sets the currency and payroll framework.")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#9CA3AF]">{t("Business name")}</label>
              <Input data-testid="add-business-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("e.g. Paris Bistro")} className="bg-[#111113] border-white/10 mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#9CA3AF]">{t("Country")}</label>
              <select data-testid="add-business-country" data-no-translate value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value })}
                className="w-full bg-[#111113] border border-white/10 rounded-sm text-[#F3F4F6] px-3 py-2.5 text-sm focus:border-[#D4AF37] mt-1">
                {(countries.length ? countries : [{ code: "GB", name: "United Kingdom", verified: true }]).map((c) => <option key={c.code} value={c.code}>{formatCountryOption(c, dict)}</option>)}
              </select>
              <p className="text-[10px] text-white/30 mt-1">{
                ((countries.length ? countries : [{ code: "GB", name: "United Kingdom", verified: true }]).find((c) => c.code === form.country_code) || {}).verified
                  ? t("Country is the source of truth — sets currency and its verified payroll framework.")
                  : t("Sets currency & operations now — automated payroll for this country is coming soon.")
              }</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#9CA3AF]">{t("Venue type (optional)")}</label>
              <select data-testid="add-business-type" value={form.restaurant_type} onChange={(e) => setForm({ ...form, restaurant_type: e.target.value })}
                className="w-full bg-[#111113] border border-white/10 rounded-sm text-[#F3F4F6] px-3 py-2.5 text-sm focus:border-[#D4AF37] mt-1">
                <option value="">—</option>
                {["Fine Dining", "Casual Dining", "Café", "Pub", "Hotel Restaurant", "Fast Food / QSR", "Bakery", "Cocktail Bar", "Wine Bar"].map((tp) => <option key={tp} value={tp}>{tp}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="add-business-save" onClick={addBusiness} disabled={busy} className="bg-[#D4AF37] text-black font-semibold rounded-sm px-5 py-2.5 text-sm hover:bg-[#FBECA1] disabled:opacity-50 inline-flex items-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("Add business")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
